<template>
    <div class="automation-wizard-field-document-type-status">
        <CompassFormItem v-bind="formItemProps" :error="field.currentError || ''">
            <CompassSelect
                v-bind="selectProps"
                v-model="modelValue"
                :disabled="disabled"
                :type="isStatusField ? CompassEnums.SelectType.TAG : CompassEnums.SelectType.TEXT"
                menuClass="automation-wizard-document-type-status-select"
            >
                <CompassDropdownOption
                    v-for="option in options"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                    :color="option.color"
                    :tagIcon="option.icon"
                >
                    <template v-if="isStatusField" #default>
                        <CompassTag
                            :color="option.color"
                            :icon="option.icon"
                            :size="CompassEnums.TagSize.SMALL"
                        >
                            {{ option.label }}
                        </CompassTag>
                    </template>
                </CompassDropdownOption>
            </CompassSelect>
        </CompassFormItem>
    </div>
</template>

<script setup lang="ts">
import { computed, watch, inject } from 'vue';
import type { Translator } from 'types/injects/translator.type';
import { allStatus } from 'Dictionaries/statusColors';
import SalesDocumentsStatus from 'Config/authenticated/SalesDocumentsStatus';

const $t = inject('$t') as Translator;

const STATUS_COLORS: Record<string, { color: string; icon?: string }> = allStatus;

interface StatusDefinition {
    label: string;
    shortLabel: string;
}

const salesDocumentsStatus = SalesDocumentsStatus as Record<
    string,
    Record<string, StatusDefinition>
>;

const EXCLUDED_STATUSES: ReadonlySet<string> = new Set(['draft']);

interface Field {
    id: string;
    type: string;
    required?: boolean;
    disabled?: boolean;
    currentError?: string;
    props?: {
        formItem?: Record<string, unknown>;
        select?: Record<string, unknown>;
    };
}

interface Props {
    field: Field;
    formData?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
    formData: () => ({}),
});

const modelValue = defineModel<string | number>({ default: '' });

const DOCUMENT_TYPES: { value: string; labelKey: string }[] = [
    { value: 'invoice', labelKey: 'common.labels.invoice' },
    { value: 'estimate', labelKey: 'common.labels.estimate' },
    { value: 'order', labelKey: 'common.labels.order' },
    { value: 'delivery', labelKey: 'common.labels.delivery' },
];

const isStatusField = computed<boolean>(() => {
    return props.field.id?.includes('documentStatus') || false;
});

const selectedDocumentType = computed<string | null>(() => {
    if (!isStatusField.value) {
        return null;
    }
    return (props.formData.documentType as string) || null;
});

const disabled = computed<boolean>(() => {
    return props.field.disabled || (isStatusField.value && !selectedDocumentType.value);
});

const options = computed<{ value: string; label: string; color?: string; icon?: string }[]>(() => {
    if (isStatusField.value) {
        const docType = selectedDocumentType.value;
        if (!docType || !salesDocumentsStatus[docType]) {
            return [];
        }
        return Object.entries(salesDocumentsStatus[docType])
            .filter(([value]) => !EXCLUDED_STATUSES.has(value))
            .map(([value, { shortLabel }]) => ({
                value,
                label: shortLabel,
                color: STATUS_COLORS[value]?.color,
                icon: STATUS_COLORS[value]?.icon,
            }));
    }
    return DOCUMENT_TYPES.map((docType) => ({
        value: docType.value,
        label: $t.__(docType.labelKey),
    }));
});

const formItemProps = computed<Record<string, unknown>>(() => {
    return {
        ...props.field.props?.formItem,
        label: props.field.props?.formItem?.label || '',
        required: props.field.required || false,
        requiredLabel:
            (props.field.props?.formItem?.requiredLabel as string) ||
            $t.__('common.labels.required'),
    };
});

const selectProps = computed<Record<string, unknown>>(() => {
    return {
        ...props.field.props?.select,
    };
});

watch(selectedDocumentType, (newDocType, oldDocType) => {
    if (isStatusField.value && oldDocType && oldDocType !== newDocType && modelValue.value) {
        modelValue.value = '';
    }
});
</script>

<style lang="scss" scoped>
.automation-wizard-field-document-type-status {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);
}
</style>

<style lang="scss">
#compass-dropdown-container {
    .automation-wizard-document-type-status-select {
        z-index: 1001;
    }
}
</style>
